import { FC, useEffect, useState } from 'react';
import BasicModalLayout from '../../../../shared/components/modal/BasicModalLayout';
import { CountriesLimitedOptions } from './CountriesLimitedOptions';
import { LimitedSharingOption } from './LimitedSharingOption';
import { Button, ButtonSize, ButtonTheme } from '../../../../shared/common/Button';
import { CountryOut } from '../../../../store/apiLibx';
import { useTranslation } from 'react-i18next';

const RESTRICTION_LEVEL_LIMITED = 'LIMITED';
const RESTRICTION_LEVEL_WORLD = 'WORLD';

interface SharingOptions {
    shareWorldwide: boolean;
    countryCodes?: string[];
}

interface Props {
    defaultCountryCode: string;
    countries: CountryOut[];
    title: string;
    show: boolean;
    productId: string;
    selectedCountries: string[];
    onFinish: (productId: string, sharingOptions: SharingOptions, modifiedCountries: boolean) => void;
    onAbort: () => void;
}

export const LibxSettingsPopup: FC<Props> = props => {
    const { t } = useTranslation();
    const [restriction, setRestriction] = useState(RESTRICTION_LEVEL_WORLD);
    const [countries, setCountries] = useState<any>([props.defaultCountryCode]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [modifiedCountries, setModifiedCountries] = useState(false);
    const [removedCountries, setRemovedCountries] = useState(false);

    useEffect(() => {
        if (props.selectedCountries) {
            setRestriction(!props.selectedCountries ? RESTRICTION_LEVEL_LIMITED : RESTRICTION_LEVEL_WORLD);
            setCountries((...prevCountries) => [...prevCountries, ...props.selectedCountries]);
        }
    }, [props.selectedCountries, props.show]);

    function setDefaultState() {
        setRestriction(RESTRICTION_LEVEL_WORLD);
        setCountries([]);
        setShowConfirm(false);
        setModifiedCountries(false);
        setRemovedCountries(false);
    }

    function onChangeSharingRestriction(restriction) {
        setRestriction(restriction);
        setCountries(props.countries.map(c => c.code));
    }

    function onSelectAllCountries() {
        setCountries(props.countries.map(c => c.code));
    }

    function onChangeSelectedCountry(countryCode) {
        const rCountries = removedCountries || countries.indexOf(countryCode) >= 0;
        const newCountries = rCountries
            ? countries.filter(c => c !== countryCode)
            : countries.slice().concat(countryCode);
        setCountries(newCountries);
        setModifiedCountries(true);
    }

    function onDeselectAllCountries() {
        setCountries([props.defaultCountryCode]);
    }

    function onFinish() {
        const sharingOptions: SharingOptions = {
            shareWorldwide: restriction === RESTRICTION_LEVEL_WORLD || countries.length === props.countries.length
        };

        if (!sharingOptions.shareWorldwide) {
            sharingOptions.countryCodes = countries;
        }

        props.onFinish(props.productId, sharingOptions, modifiedCountries);
        setDefaultState();
    }

    function onFinishConfirm() {
        if (removedCountries) {
            setShowConfirm(true);
        } else {
            onFinish();
        }
    }

    const contentSettings = (
        <span>
            <LimitedSharingOption
                onChangeSharingRestriction={onChangeSharingRestriction}
                selectedRestriction={restriction}
            />
            {restriction === RESTRICTION_LEVEL_LIMITED ? (
                <CountriesLimitedOptions
                    defaultCountryCode={props.defaultCountryCode}
                    countries={props.countries ?? []}
                    selectedCountries={countries ?? []}
                    onToggleCountry={onChangeSelectedCountry}
                    onSelectAllCountries={onSelectAllCountries}
                    onDeselectAllCountries={onDeselectAllCountries}
                />
            ) : null}
        </span>
    );

    const finishButton = (
        <Button
            size={ButtonSize.LARGE}
            text={t('yourLocalContent.yourTitles.finish')}
            onClick={onFinishConfirm}
            className="bt2 width-90px"
        />
    );

    const removeButton = (
        <Button
            size={ButtonSize.LARGE}
            theme={ButtonTheme.DANGER}
            text={t('yourLocalContent.yourTitles.remove')}
            onClick={onFinish}
            className="bt2 width-90px"
        />
    );

    const cancelRemoveButton = (
        <Button
            size={ButtonSize.LARGE}
            theme={ButtonTheme.NEUTRAL}
            onClick={() => {
                setShowConfirm(false);
                setDefaultState();
            }}
            text={t('cancel')}
            className="bt2 width-90px"
        />
    );

    const cancelSettingsButton = (
        <Button
            size={ButtonSize.LARGE}
            theme={ButtonTheme.NEUTRAL}
            onClick={props.onAbort}
            text={t('cancel')}
            className="bt2 width-90px"
        />
    );

    const removeHeader = <h2>{t('yourLocalContent.yourTitles.confirmations.removeHeader')}</h2>;

    const contentRemoveMessage = (
        <span style={{ fontSize: '15px' }}>{t('yourLocalContent.yourTitles.confirmations.removeConfirmation')}</span>
    );

    return (
        <>
            <BasicModalLayout
                visible={props.show}
                onClose={props.onAbort}
                header={<h2>{props.title}</h2>}
                content={contentSettings}
                okButton={finishButton}
                cancelButton={cancelSettingsButton}
                width={600}
            />
            <BasicModalLayout
                visible={showConfirm}
                onClose={() => setShowConfirm(false)}
                header={removeHeader}
                content={contentRemoveMessage}
                okButton={removeButton}
                cancelButton={cancelRemoveButton}
                width={600}
            />
        </>
    );
};
